import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { matchPath } from 'react-router-dom'
import { moduleContext } from 'react-contextual'
import { get } from 'lodash-es'
import { ROUTES } from 'constants/routes'
import { uiEvents } from '../../ui'
import { layoutEvents, layoutSelectors } from '../../Layout/state'
import { partyApiEvents, partyApiSelectors } from '../../api/party'
import { getContactFilterSearch } from '../filters'
import * as selectors from './selectors'
import { contactsEvents } from './'
import { contactsSelectors } from '..'
import history from '../../../state/history'
import { apiStateSelectors } from '../../api/apiState'
import { downloadBase64 } from 'utils/fileUtils'
import { userApiSelectors } from 'modules/api/user'
import { walletApiSelectors } from 'modules/api/wallet'

const ContactsProvider = moduleContext()(({ context, children, ...props }: any) => {
  const store = useStore()
  const dispatch = useDispatch()

  const searchQuery = useSelector(selectors.getSearchQuery)
  const { match } = props
  const { params } = match
  const id = get(params, 'id')
  const location = useSelector(state => get(state, 'router.location', ''))
  const contactFilterSearch = useSelector((state: unknown) =>
    getContactFilterSearch(get(state, 'router.location.search', '')),
  )

  /** @todo figure out how to curry selectors!?!?!?!?!? */
  const state = store.getState()
  const currentParty = partyApiSelectors.getPartyDetailsById(state)(id)
  const currentPartyType = partyApiSelectors.getPartyTypeById(state)(id)
  const [currentAccountId, setCurrentAccountId] = useState(null)
  const partyHasBankDetails = partyApiSelectors.partyHasBankDetails(state)(id)
  const getPartyAccountByAccountId = useSelector(partyApiSelectors.getPartyAccountByAccountId)
  const partyAccount = getPartyAccountByAccountId(id, currentAccountId)
  const partyAccountBalance = walletApiSelectors.getBalance(state)(id, currentAccountId, partyAccount?.tag)
  const partyAccountAsset = walletApiSelectors.getBalanceAsset(state)(id, currentAccountId, partyAccount?.tag)

  // Fetch the full party entity (incl. accounts) whenever a contact id is in the
  // route. The list only loads partial parties via quickSearch, so without this the
  // account panels never mount on direct navigation/paste and balance_request never fires.
  // Guard on the route: ContactsProvider is reused on /leases/:id (PortfoliosRoute) and
  // /properties (PropertiesRoute) where match.params.id is the portfolio/property id, not a
  // party id — dispatching party_request there fires a spurious request on the lease id.
  const pathname = get(location, 'pathname', '')
  useEffect(() => {
    const isContactRoute = matchPath(pathname, { path: ROUTES.contactView })
    if (id && isContactRoute) {
      dispatch(partyApiEvents.party_request(id))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, pathname])

  const contactsStore = {
    id,
    match,
    searchQuery,
    currentParty,
    currentPartyType,
    partyHasBankDetails,
    partyAccountBalance,
    partyAccountAsset,
    currentAccountId,
    setCurrentAccountId,
    currentPartyBalance: 0,
    location,
    contactFilterSearch,
    isReadOnly: useSelector(userApiSelectors.isReadOnlyRole),
    isLoading: useSelector(state => apiStateSelectors.isLoading(state)([partyApiEvents.quickSearch_request])),
    // Scoped to this contact. The apiState flag is one global boolean for every party
    // fetch, so an unrelated party resolving first cleared it mid-load and the view
    // rendered empty.
    isContactViewLoading: useSelector(state => partyApiSelectors.isPartyLoading(state)(id)),
    filters: useSelector(selectors.contactsFilters),
    currentContactInfo: contactsSelectors.getContactInfo(state)(id),
    primaryPanel: layoutSelectors.getPrimaryPanel(state)('contacts'),
    redirect: (path: any) => dispatch(uiEvents.redirect(path)),
    /** @todo move to general app provider */
    goBack: () => history.goBack(),
    openContact: (id: any) => {
      dispatch(uiEvents.redirect(`/contacts/${id}${contactFilterSearch}`))
      // party_request is now dispatched by the id-driven effect above
      dispatch(layoutEvents.openPrimaryPanel('contacts'))
      dispatch(layoutEvents.closeSidebar('global'))
    },
    openContactFromNonContactsRoute: (id: any) => {
      dispatch(uiEvents.redirect(`/contacts/${id}`))
      dispatch(layoutEvents.openPrimaryPanel('contacts'))
      dispatch(layoutEvents.closeSidebar('global'))
    },
    openContactStatement: (accountId: any) => {
      dispatch(uiEvents.redirect(`/contacts/${id}/${accountId}/statement${contactFilterSearch}`))
    },
    openContactConsolidatedStatement: () => {
      dispatch(uiEvents.redirect(`/contacts/consolidated/${id}/statement${contactFilterSearch}`))
    },
    closeContact: () => {
      dispatch(uiEvents.redirect(`/contacts${contactFilterSearch}`))
      dispatch(layoutEvents.closePrimaryPanel('contacts'))
      dispatch(layoutEvents.openSidebar('global'))
    },
    downloadStatement: (filename: any, base64: any) => downloadBase64(base64, 'application/pdf', filename),
    getContacts: (query = '', tags = '') => dispatch(partyApiEvents.quickSearch_request({ query, tags })),
    onQueryChange: (e: any) => dispatch(contactsEvents.searchQueryUpdated(e.target.value)),
    fetchParty: (id: any) => dispatch(partyApiEvents.party_request(id)),
    updateParty: (payload: any) => dispatch(partyApiEvents.updateParty_request(payload)),
    createParty: (payload: any) => dispatch(partyApiEvents.createParty_request(payload)),
    ...props,
  }
  return <context.Provider value={contactsStore}>{children}</context.Provider>
})

export default ContactsProvider
